import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ListChecks, RefreshCw, Trophy, Wand2 } from "lucide-react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { PageHeader } from "@/components/PageHeader";
import { StatusPill } from "@/components/StatusPill";
import { ProjectsApi, TasksApi } from "@/lib/api";
import { formatBytes, formatDuration } from "@/lib/utils";

type StatusFilter = "all" | "pending" | "tagged";

export function TaskListPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const pid = Number(projectId);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");

  const project = useQuery({ queryKey: ["project", pid], queryFn: () => ProjectsApi.get(pid), enabled: Number.isFinite(pid) });
  const tasks = useQuery({
    queryKey: ["tasks", pid, statusFilter],
    queryFn: () => TasksApi.list(pid, statusFilter === "all" ? undefined : { status: statusFilter }),
    enabled: Number.isFinite(pid),
  });
  const leaderboard = useQuery({
    queryKey: ["leaderboard", pid],
    queryFn: () => ProjectsApi.leaderboard(pid, 3),
    enabled: Number.isFinite(pid),
  });

  const nextTask = useMutation({
    mutationFn: () => TasksApi.next(pid),
    onSuccess: (task) => navigate(`/projects/${pid}/tasks/${task.id}`),
  });

  return (
    <>
      <PageHeader
        title="Tasks"
        subtitle={project.data?.name}
        actions={
          <>
            <button className="btn-ghost" onClick={() => qc.invalidateQueries({ queryKey: ["tasks", pid] })} title="Refresh">
              <RefreshCw size={16} />
            </button>
            <button
              className="btn-primary"
              onClick={() => nextTask.mutate()}
              disabled={nextTask.isPending}
              title="Auto-assign next task"
            >
              <Wand2 size={16} />
              {nextTask.isPending ? "Loading…" : "Next task"}
            </button>
          </>
        }
      />

      <div className="px-4 py-4 md:px-8 md:py-6">
        {nextTask.isError && (
          <div className="mb-4 rounded-md border border-status-warn/40 bg-status-warn/10 px-3 py-2 text-sm text-status-warn">
            {(nextTask.error as { response?: { status?: number } })?.response?.status === 404
              ? "No more tasks for you in this project — every task already has your submission."
              : "Failed to fetch next task."}
          </div>
        )}

        {project.data && (
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <div className="flex gap-2 text-sm">
              <span className="rounded-md border border-border bg-surface px-3 py-1.5">
                Total <span className="ml-1 font-semibold">{project.data.total_tasks}</span>
              </span>
              <span className="rounded-md border border-border bg-surface px-3 py-1.5">
                Pending <span className="ml-1 font-semibold">{project.data.total_tasks - project.data.tagged_tasks}</span>
              </span>
              <span className="rounded-md border border-border bg-surface px-3 py-1.5">
                Tagged <span className="ml-1 font-semibold">{project.data.tagged_tasks}</span>
              </span>
              <span className="rounded-md border border-accent/40 bg-accent/10 px-3 py-1.5 text-accent">
                My completed <span className="ml-1 font-semibold">{project.data.my_submitted_tasks}</span>
              </span>
            </div>
            <div className="ml-auto flex items-center gap-2 text-sm">
              <label htmlFor="status-filter" className="text-text-muted">Status</label>
              <select
                id="status-filter"
                className="input h-8 py-0 text-sm"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              >
                <option value="all">All ({project.data.total_tasks})</option>
                <option value="pending">Pending ({project.data.total_tasks - project.data.tagged_tasks})</option>
                <option value="tagged">Tagged ({project.data.tagged_tasks})</option>
              </select>
            </div>
          </div>
        )}

        {leaderboard.data && leaderboard.data.length > 0 && (
          <div className="mb-4 flex items-center gap-3 rounded-md border border-border bg-surface px-3 py-2 text-sm">
            <Trophy size={16} className="text-status-warn" />
            <span className="font-medium text-text-muted">Top contributors:</span>
            {leaderboard.data.map((entry, i) => {
              const medal = ["bg-status-warn/20 text-status-warn", "bg-accent/15 text-accent", "bg-status-pending/15 text-text-muted"][i] ?? "bg-surface text-text";
              return (
                <span key={entry.user_id} className={`inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-xs font-medium ${medal}`}>
                  <span className="opacity-70">#{i + 1}</span>
                  {entry.display_name || entry.username}
                  <span className="font-semibold">{entry.submitted_count}</span>
                </span>
              );
            })}
          </div>
        )}

        <div className="card overflow-hidden">
          {tasks.isLoading && <div className="p-6 text-sm text-text-muted">Loading tasks…</div>}
          {tasks.data?.length === 0 && (
            <div className="p-10 text-center text-sm text-text-muted">
              <ListChecks className="mx-auto mb-3 opacity-40" size={32} />
              No tasks yet. Open project settings and click <span className="text-text">Scan S3 prefix</span>.
            </div>
          )}

          {/* Desktop table — unchanged at md+. */}
          <div className="hidden md:block">
            {(tasks.data?.length ?? 0) > 0 && (
              <div className="data-row grid-cols-[minmax(0,3fr)_1fr_1fr_1fr_1fr_1fr] text-xs font-semibold uppercase tracking-wider text-text-dim">
                <div>File</div>
                <div>Duration</div>
                <div>Size</div>
                <div>Status</div>
                <div>Taggings</div>
                <div>My state</div>
              </div>
            )}
            {tasks.data?.map((t) => (
              <div
                key={t.id}
                className="data-row grid-cols-[minmax(0,3fr)_1fr_1fr_1fr_1fr_1fr] cursor-pointer"
                onClick={() => navigate(`/projects/${pid}/tasks/${t.id}`)}
              >
                <div className="min-w-0">
                  <div className="truncate font-medium">{t.file_name}</div>
                  <div className="truncate font-mono text-[11px] text-text-dim">{t.s3_key}</div>
                </div>
                <div className="text-sm text-text-muted">{formatDuration(t.duration_seconds)}</div>
                <div className="text-sm text-text-muted">{formatBytes(t.size_bytes)}</div>
                <div><StatusPill status={t.status} /></div>
                <div className="text-sm text-text-muted">{t.tagging_count}</div>
                <div><StatusPill status={t.my_state} /></div>
              </div>
            ))}
          </div>

          {/* Mobile cards — one tappable card per task. */}
          <div className="divide-y divide-border-muted md:hidden">
            {tasks.data?.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => navigate(`/projects/${pid}/tasks/${t.id}`)}
                className="flex w-full flex-col gap-2 px-4 py-3 text-left active:bg-bg-elevated"
              >
                <div className="min-w-0">
                  <div className="truncate font-medium">{t.file_name}</div>
                  <div className="truncate font-mono text-[11px] text-text-dim">{t.s3_key}</div>
                </div>
                <div className="flex flex-wrap items-center gap-2 text-xs text-text-muted">
                  <StatusPill status={t.status} />
                  <StatusPill status={t.my_state} />
                  <span>{formatDuration(t.duration_seconds)}</span>
                  <span>· {formatBytes(t.size_bytes)}</span>
                  <span>· {t.tagging_count} tagging{t.tagging_count === 1 ? "" : "s"}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
